import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { notificationAPI, getErrorMessage } from '../utils/api';
import { formatDate } from '../utils/helpers';

const REFRESH_MS = 60000;
const MAX_ROWS = 10;

const NotificationsBell = ({ className = '', iconClassName = '' }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await notificationAPI.getAll();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      // Background refresh; don't toast each failure.
      // eslint-disable-next-line no-console
      console.error('Notifications fetch failed:', error);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const unread = items.filter((n) => !n.is_read);
  const badge = unread.length > 99 ? '99+' : unread.length;

  const markOne = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to mark as read'));
    }
  };

  const markAll = async () => {
    if (!unread.length) return;
    setMarking(true);
    try {
      await notificationAPI.markAllRead();
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to mark all as read'));
    } finally {
      setMarking(false);
    }
  };

  const recent = items.slice(0, MAX_ROWS);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative ${className}`}
          aria-label={`Notifications${unread.length ? ` (${unread.length} unread)` : ''}`}
        >
          <Bell className={`w-5 h-5 ${iconClassName}`} />
          {unread.length > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
              {badge}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-semibold">Notifications</span>
          <button
            type="button"
            onClick={markAll}
            disabled={marking || unread.length === 0}
            className="text-xs text-blue-600 disabled:text-gray-400 hover:underline"
          >
            {marking ? 'Marking…' : 'Mark all read'}
          </button>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {recent.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-gray-500">
              No notifications yet
            </div>
          ) : (
            recent.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => {
                  if (!n.is_read) markOne(n.id);
                }}
                className={`w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-slate-800 ${
                  n.is_read ? 'opacity-70' : 'bg-blue-50/40 dark:bg-slate-900/40'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{n.title}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2">
                      {n.message}
                    </p>
                    <p className="mt-1 text-[10px] text-gray-400">
                      {formatDate(n.created_at)}
                    </p>
                  </div>
                  {n.is_read ? (
                    <Check className="w-3.5 h-3.5 text-gray-400 mt-1" />
                  ) : (
                    <span className="mt-1 w-2 h-2 rounded-full bg-blue-500" />
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        <div className="border-t px-3 py-2 text-center">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/user/notifications');
            }}
            className="text-xs text-blue-600 hover:underline"
          >
            View all
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsBell;
